import booleanIntersects from "@turf/boolean-intersects";
import { degreesToRadians, earthRadius, feature as asFeature } from "@turf/helpers";
import type { BBox, LineString, MultiLineString, MultiPolygon, Polygon, Position } from "geojson";
import { sortBy } from "lodash";

import { distanceSqAtFraction, firstVertex, polygonRings, projectionFraction, toLineStrings } from "./geometry";

/**
 * Fast proximity tests between parcels and streets.
 *
 * Every test projects its inputs into a planar frame centred on the parcel, then finds the nearest vertex-to-segment
 * pair, pruning by bounding box and stopping as soon as the answer is known. The frame is only accurate within a few
 * kilometres of its origin, which is ample for the distances involved here.
 *
 * Distances in metres use a frame scaled by metres-per-degree at the origin's latitude; the gate distance is planar
 * degrees (to match the training pipeline), so it uses an unscaled frame instead.
 */

/** Metres per degree of latitude (and of longitude at the equator). */
const METRES_PER_DEGREE = degreesToRadians(1) * earthRadius;

type LocalPosition = [number, number];

/** A projection of lat/lon positions into a planar frame. */
type LocalFrame = (p: Position) => LocalPosition;

/**
 * Return a frame, in metres, centred on `origin`.
 *
 *          north (m)
 *            ^
 *            |    . p = [x, y]
 *            |
 *     origin o---------> east (m)
 */
const metresFrame =
  (origin: Position): LocalFrame =>
  (p) => [(p[0] - origin[0]) * METRES_PER_DEGREE * Math.cos(degreesToRadians(origin[1])), (p[1] - origin[1]) * METRES_PER_DEGREE];

/** Return a frame, in planar degrees, centred on `origin`. */
const degreesFrame =
  (origin: Position): LocalFrame =>
  (p) => [p[0] - origin[0], p[1] - origin[1]];

/** Return the given bbox expanded by at least `metres` on every side. */
export function expandBboxMetres([minX, minY, maxX, maxY]: BBox, metres: number): BBox {
  const dLat = metres / METRES_PER_DEGREE;
  const dLng = dLat / Math.cos(degreesToRadians(Math.max(Math.abs(minY), Math.abs(maxY))));
  return [minX - dLng, minY - dLat, maxX + dLng, maxY + dLat];
}

/** Return the given bbox expanded by `degrees` on every side. */
export const expandBboxDegrees = ([minX, minY, maxX, maxY]: BBox, degrees: number): BBox => [
  minX - degrees,
  minY - degrees,
  maxX + degrees,
  maxY + degrees,
];

/**
 * Return the squared distance from `p` to the segment `ab`.
 *
 * For three example positions of `p` (beyond either end, the distance is to the nearer endpoint):
 *
 *   p1           p2
 *     \          |
 *      a=========+=====b
 *                       \
 *                        p3
 */
const segmentDistanceSq = (p: LocalPosition, a: LocalPosition, b: LocalPosition): number =>
  distanceSqAtFraction(p, a, b, Math.max(0, Math.min(1, projectionFraction(p, a, b) ?? 0)));

type LocalBBox = [number, number, number, number];

/** A polyline in a local frame, with its bounding box. */
type LocalPath = { points: LocalPosition[]; bbox: LocalBBox };

/** Return the bounding box of the given points. */
const bboxOf = (points: LocalPosition[]): LocalBBox => {
  const bbox: LocalBBox = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [x, y] of points) {
    bbox[0] = Math.min(bbox[0], x);
    bbox[1] = Math.min(bbox[1], y);
    bbox[2] = Math.max(bbox[2], x);
    bbox[3] = Math.max(bbox[3], y);
  }
  return bbox;
};

const localPath = (coordinates: Position[], frame: LocalFrame): LocalPath => {
  const points = coordinates.map(frame);
  return { points, bbox: bboxOf(points) };
};

/**
 * Return the squared distance between two boxes (zero if they overlap).
 *
 *   +-------+
 *   |   a   |
 *   +-------+
 *           .  dy
 *           . . . . . +-------+      result = dx² + dy²
 *               dx    |   b   |
 *                     +-------+
 */
const bboxDistanceSq = (a: LocalBBox, b: LocalBBox): number => {
  const dx = Math.max(0, a[0] - b[2], b[0] - a[2]);
  const dy = Math.max(0, a[1] - b[3], b[1] - a[3]);
  return dx * dx + dy * dy;
};

/**
 * Return the squared distance from the nearest of `vertices` to any segment of `path`, or `boundSq` if no pair
 * is closer than that.
 */
const verticesToPathDistanceSq = (
  vertices: LocalPosition[],
  verticesBbox: LocalBBox,
  path: LocalPosition[],
  boundSq: number,
  stopBelowSq: number
): number => {
  let best = boundSq;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];

    // the bounding box distance from the vertices to the segment `ab` is a lower bound on the distance from any
    // vertex to that segment, so we can save a little work by skipping any segment that is already further away
    // than the best distance found so far.
    if (bboxDistanceSq(bboxOf([a, b]), verticesBbox) < best) {
      for (const p of vertices) {
        const d = segmentDistanceSq(p, a, b);
        if (d < best) {
          best = d;
          if (best < stopBelowSq) return best;
        }
      }
    }
  }
  return best;
};

/**
 * Return the squared distance between the nearest pair of paths from `a` and `b`, or `boundSq` if no pair is
 * closer than that.
 *
 * The paths are assumed not to intersect, so the distance is attained between a vertex of one and an edge of the
 * other. The search stops as soon as a pair closer than `stopBelowSq` is found.
 *
 *   a   o--------o
 *                 \
 *                  o---------o
 *                  :
 *                  :  <- sqrt(result)
 *   b       o------+---------------o
 */
const pathsDistanceSq = (a: LocalPath[], b: LocalPath[], boundSq: number, stopBelowSq: number): number => {
  let best = boundSq;
  for (const pa of a) {
    for (const pb of b) {
      // The shortest distance between two paths is at least the distance between their bounding boxes,
      // so we can skip any pair that are already further apart than the best distance found so far.
      if (bboxDistanceSq(pa.bbox, pb.bbox) < best) {
        best = verticesToPathDistanceSq(pa.points, pa.bbox, pb.points, best, stopBelowSq);
        if (best < stopBelowSq) return best;
        best = verticesToPathDistanceSq(pb.points, pb.bbox, pa.points, best, stopBelowSq);
        if (best < stopBelowSq) return best;
      }
    }
  }
  return best;
};

const localRings = (polygon: Polygon | MultiPolygon, frame: LocalFrame): LocalPath[] => polygonRings(polygon).map((ring) => localPath(ring, frame));

const localLines = (lines: ReadonlyArray<LineString | MultiLineString>, frame: LocalFrame): LocalPath[] =>
  lines.flatMap((line) => toLineStrings(line).map((l) => localPath(l.coordinates, frame)));

/** Return whether any pair of paths from `a` and `b` comes within `limit` (in the paths' frame units) of each other. */
const pathsWithin = (a: LocalPath[], b: LocalPath[], limit: number): boolean => {
  const limitSq = limit * limit;
  return pathsDistanceSq(a, b, limitSq, limitSq) < limitSq;
};

/** Return whether the line comes within `limit` (in the frame's units) of the polygon. */
const lineWithin = (polygon: Polygon | MultiPolygon, line: LineString | MultiLineString, limit: number, frame: LocalFrame): boolean =>
  booleanIntersects(asFeature(line), asFeature(polygon)) || pathsWithin(localRings(polygon, frame), localLines([line], frame), limit);

/** Return whether any of the lines comes within `metres` of the point. */
export function pointWithinMetres(point: Position, lines: ReadonlyArray<LineString | MultiLineString>, metres: number): boolean {
  const frame = metresFrame(point);
  return pathsWithin([localPath([point], frame)], localLines(lines, frame), metres);
}

/** Return whether the line comes within `metres` of the polygon. */
export const lineWithinMetres = (polygon: Polygon | MultiPolygon, line: LineString | MultiLineString, metres: number): boolean =>
  lineWithin(polygon, line, metres, metresFrame(firstVertex(polygon)));

/** Return whether the line comes within `degrees` (planar) of the polygon. */
export const lineWithinDegrees = (polygon: Polygon | MultiPolygon, line: LineString | MultiLineString, degrees: number): boolean =>
  lineWithin(polygon, line, degrees, degreesFrame(firstVertex(polygon)));

/**
 * Return the distance (metres) from the polygon to the nearest of the lines, or `null` if none lies within
 * `limitMetres`.
 *
 * The polygon is assumed not to intersect any of the lines.
 */
export function nearestLineWithinMetres(
  polygon: Polygon | MultiPolygon,
  lines: ReadonlyArray<LineString | MultiLineString>,
  limitMetres: number
): number | null {
  const frame = metresFrame(firstVertex(polygon));
  const rings = localRings(polygon, frame);
  const polygonBbox = bboxOf(rings.flatMap((r) => r.points));

  // sort from closest to farthest by bounding box distance.
  const candidates = sortBy(
    localLines(lines, frame).map((path) => ({ path, lowerBoundSq: bboxDistanceSq(path.bbox, polygonBbox) })),
    (c) => c.lowerBoundSq
  );

  const limitSq = limitMetres * limitMetres;
  let bestSq = limitSq;
  for (const { path, lowerBoundSq } of candidates) {
    // we can terminate the search early if the lower bound on the distance is already worse than
    // the best distance found so far (because the candidates are in bounding box distance order).
    if (lowerBoundSq >= bestSq) break;
    bestSq = pathsDistanceSq(rings, [path], bestSq, 0);
  }
  return bestSq < limitSq ? Math.sqrt(bestSq) : null;
}
